import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    location: "",
    product_required: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/enquiry`, formData);
      toast.success("Enquiry submitted successfully! We'll contact you soon.");
      setFormData({
        name: "",
        company_name: "",
        contact_number: "",
        email: "",
        location: "",
        product_required: "",
        message: "",
      });
    } catch (error) {
      console.error("Enquiry submission error:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-main to-primary-dark text-white py-20" data-testid="contact-hero-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 tracking-tight"
          >
            CONTACT US
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-body max-w-3xl mx-auto opacity-90"
          >
            Get in touch with us for enquiries and support
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-white" data-testid="contact-content-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-heading font-bold text-primary-main mb-6">
                GET IN TOUCH
              </h2>
              <p className="text-base text-secondary-main font-body leading-relaxed mb-8">
                Have questions or need assistance? Reach out to us and our team will get back to you as soon as possible.
              </p>

              {/* Contact Details */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-primary-main/10 p-3 rounded-sm">
                    <Phone className="text-primary-main" size={24} />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-secondary-dark mb-1">Phone</p>
                    <a
                      href="tel:+917305955155"
                      className="text-base text-secondary-main hover:text-primary-main transition-colors"
                      data-testid="contact-phone"
                    >
                      +91 73059 55155
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-primary-main/10 p-3 rounded-sm">
                    <Mail className="text-primary-main" size={24} />
                  </div>
                  <div>
                    <p className="font-body font-semibold text-secondary-dark mb-1">Email</p>
                    <a
                      href="mailto:sale@voninda.in"
                      className="text-base text-secondary-main hover:text-primary-main transition-colors"
                      data-testid="contact-email"
                    >
                      sale@voninda.in
                    </a>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-8 bg-gray-100 border border-gray-300 rounded-sm h-64 flex items-center justify-center" data-testid="map-placeholder">
                <p className="text-secondary-light font-body">Map location will be integrated here</p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gray-50 border border-gray-200 rounded-sm p-8"
            >
              <h3 className="text-2xl font-heading font-bold text-primary-main mb-6">
                SEND ENQUIRY
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                    data-testid="contact-input-name"
                    placeholder="Your full name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    required
                    data-testid="contact-input-company"
                    placeholder="Your company name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Contact Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    value={formData.contact_number}
                    onChange={(e) => handleChange("contact_number", e.target.value)}
                    required
                    data-testid="contact-input-phone"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                    data-testid="contact-input-email"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleChange("location", e.target.value)}
                    required
                    data-testid="contact-input-location"
                    placeholder="City, State"
                  />
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Product Required <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.product_required}
                    onValueChange={(value) => handleChange("product_required", value)}
                    required
                  >
                    <SelectTrigger data-testid="contact-select-product">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single Phase Servo Stabilizer">Single Phase Servo Stabilizer</SelectItem>
                      <SelectItem value="Three Phase Servo Stabilizer">Three Phase Servo Stabilizer</SelectItem>
                      <SelectItem value="Three Phase Oil Cooled Servo Stabilizer">
                        Three Phase Oil Cooled Servo Stabilizer
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-body font-medium text-secondary-dark mb-1">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    required
                    rows={4}
                    data-testid="contact-input-message"
                    placeholder="Tell us about your requirements"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary-main text-white hover:bg-primary-light rounded-sm py-6 text-base"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? "Submitting..." : "Submit Enquiry"}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
